<template>
  <div class="card">
    <div class="card-header border-0 pt-6">
      <div class="card-title">
        <div class="d-flex align-items-center position-relative my-1">
          <input type="text" v-model="searchParams.search" v-on:keyup.enter="handleSearch" class="form-control form-control-solid w-250px me-2" placeholder="Search Fujifilm mappings" />
          <el-tooltip class="box-item" effect="dark" :content="constants.general.SEARCH" placement="top">
            <button type="button" @click.prevent="handleSearch" class="btn btn-icon btn-light btn-sm me-2"><i class="bi bi-search"></i></button>
          </el-tooltip>
          <el-tooltip class="box-item" effect="dark" :content="constants.general.RESET" placement="top">
            <button type="button" @click.prevent="resetSearch" class="btn btn-icon btn-light btn-sm"><i class="bi bi-arrow-counterclockwise"></i></button>
          </el-tooltip>
        </div>
      </div>
      <div class="card-toolbar">
        <button type="button" class="btn btn-secondary me-2" @click="$router.push({ name: 'trading-partners' })">{{ constants.general.CANCEL }}</button>
        <button type="button" class="btn btn-primary" v-if="permission.isEditAllowed" @click="openForm()"><i class="bi bi-plus-circle-fill fs-4"></i> {{ constants.general.ADD }}</button>
      </div>
    </div>
    <div class="card-body pt-0">
      <Datatable
        :table-data="tableData"
        :table-header="tableHeader"
        :rows-per-page="searchParams.recordsPerPage"
        :empty-table-text="noDataFound"
        :loading="loading"
        :current-page="1"
        :enable-items-per-page-dropdown="true"
        :total="total"
        ref="updateProps"
        @current-change="handleCurrentChange"
        @items-per-page-change="handleSizeChange"
      >
        <template v-slot:cell-product_sku="{ row: data }">
          {{ data.product_sku ? data.product_sku.sku : '-' }}
        </template>
        <template v-slot:cell-fujifilm_product_id="{ row: data }">
          {{ data.fujifilm_product_id }}
        </template>
        <template v-slot:cell-fujifilm_sku="{ row: data }">
          {{ data.fujifilm_sku }}
        </template>
        <template v-slot:cell-is_active="{ row: data }">
          {{ data.is_active ? 'Yes' : 'No' }}
        </template>
        <template v-slot:cell-actions="{ row: data }">
          <button type="button" v-if="permission.isEditAllowed" @click.prevent="openForm(data)" class="btn btn-icon btn-light btn-sm me-2">
            <i class="bi bi-pencil-square"></i>
          </button>
          <button type="button" v-if="permission.isDeleteAllowed" @click.prevent="remove(data.id, tableData.findIndex((i) => i.id === data.id))" class="btn btn-icon btn-light btn-sm">
            <i class="bi bi-trash text-danger"></i>
          </button>
        </template>
      </Datatable>
    </div>

    <el-dialog v-model="dialogVisible" :title="formData.id ? 'Edit Fujifilm mapping' : 'Add Fujifilm mapping'" width="50%">
      <el-form :model="formData" ref="formRef" label-position="top">
        <div class="row">
          <div class="col-md-6 mb-3">
            <label class="required">Product SKU</label>
            <el-form-item prop="product_sku_id" :rules="{ required: true, message: 'Product SKU is required', trigger: 'change' }">
              <el-select v-model="formData.product_sku_id" filterable remote reserve-keyword :remote-method="searchProductSkus" placeholder="Search SKU" size="large" class="w-100">
                <el-option v-for="sku in productSkuOptions" :key="sku.product_sku_id" :label="sku.sku" :value="sku.product_sku_id" />
              </el-select>
            </el-form-item>
          </div>
          <div class="col-md-6 mb-3">
            <label class="required">Fujifilm product ID</label>
            <el-form-item prop="fujifilm_product_id" :rules="{ required: true, message: 'Fujifilm product ID is required', trigger: 'blur' }">
              <el-input v-model="formData.fujifilm_product_id" size="large" />
            </el-form-item>
          </div>
          <div class="col-md-6 mb-3">
            <label class="required">Fujifilm SKU</label>
            <el-form-item prop="fujifilm_sku" :rules="{ required: true, message: 'Fujifilm SKU is required', trigger: 'blur' }">
              <el-input v-model="formData.fujifilm_sku" size="large" />
            </el-form-item>
          </div>
          <div class="col-md-6 mb-3">
            <label>Base price</label>
            <el-input v-model="formData.base_price" size="large" />
          </div>
          <div class="col-md-12 mb-3">
            <label>Fujifilm product options (JSON)</label>
            <el-input v-model="formData.fujifilm_product_options_json" type="textarea" :rows="4" />
          </div>
          <div class="col-md-6 mb-3">
            <label>Active</label>
            <el-switch v-model="formData.is_active" active-color="#13ce66" inactive-color="#ff4949" />
          </div>
        </div>
      </el-form>
      <template #footer>
        <button type="button" class="btn btn-secondary me-2" @click="dialogVisible = false">{{ constants.general.CANCEL }}</button>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="saveForm">{{ constants.general.SAVE }}<span v-if="saving" class="spinner-border spinner-border-sm align-middle ms-2"></span></button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from "vue";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { notificationFire } from "@/composable/notification.js";
import { ElMessageBox } from "element-plus";
import { formatText } from "@/core/helpers/common";
import globalConstant from "@/core/data/globalConstant.js";
import { loggedInUserRoles, loggedInUserPermissions } from "@/composable/get-roles.js";

export default {
  name: "fujifilm-product-mappings",
  components: { Datatable },
  setup() {
    const constants = globalConstant;
    const loading = ref(false);
    const saving = ref(false);
    const total = ref(0);
    const tableData = ref([]);
    const updateProps = ref(null);
    const dialogVisible = ref(false);
    const formRef = ref(null);
    const productSkuOptions = ref([]);
    const noDataFound = ref(constants.general.LOADINIG);
    const searchParams = reactive({
      search: "",
      recordsPerPage: Number(JSON.parse(window.localStorage.getItem("site_config"))["per_page"]),
      pageNub: 1,
    });
    const permission = reactive({
      isEditAllowed: false,
      isDeleteAllowed: false,
    });
    const formData = ref({
      id: null,
      product_sku_id: "",
      fujifilm_product_id: "",
      fujifilm_sku: "",
      base_price: "",
      fujifilm_product_options_json: "{}",
      is_active: true,
    });
    const tableHeader = ref([
      { name: constants.general.SRNO, key: "index", sortable: false },
      { name: "Product SKU", key: "product_sku", sortable: false },
      { name: "Fujifilm product ID", key: "fujifilm_product_id", sortable: true },
      { name: "Fujifilm SKU", key: "fujifilm_sku", sortable: true },
      { name: "Active", key: "is_active", sortable: false },
      { name: constants.general.ACTIONS, key: "actions", sortable: false },
    ]);

    const getMappings = async () => {
      loading.value = true;
      await ApiService.query("fujifilm-product-mappings", { params: searchParams })
        .then(({ data }) => {
          if (data.data.data && data.data.data.length) {
            tableData.value.splice(0, tableData.value.length, ...data.data.data);
            total.value = data.data.total;
            updateProps.value.updateProps(total.value, searchParams.recordsPerPage, searchParams.pageNub);
          } else {
            tableData.value = [];
            total.value = 0;
            updateProps.value.updateProps(total.value, searchParams.recordsPerPage, searchParams.pageNub, constants.general.NO_DATA_FOUND);
          }
          loading.value = false;
        })
        .catch(() => {
          tableData.value = [];
          loading.value = false;
        });
    };

    const searchProductSkus = async (query) => {
      await ApiService.query("fujifilm-product-mappings/search/product-skus", { params: { search: query || "" } })
        .then(({ data }) => {
          productSkuOptions.value = data.data || [];
        })
        .catch(() => {
          productSkuOptions.value = [];
        });
    };

    const openForm = (row = null) => {
      if (row) {
        formData.value = {
          id: row.id,
          product_sku_id: row.product_sku_id,
          fujifilm_product_id: row.fujifilm_product_id,
          fujifilm_sku: row.fujifilm_sku,
          base_price: row.base_price,
          fujifilm_product_options_json: JSON.stringify(row.fujifilm_product_options || {}, null, 2),
          is_active: !!row.is_active,
        };
        if (row.product_sku) {
          productSkuOptions.value = [row.product_sku];
        }
      } else {
        formData.value = {
          id: null,
          product_sku_id: "",
          fujifilm_product_id: "",
          fujifilm_sku: "",
          base_price: "",
          fujifilm_product_options_json: "{}",
          is_active: true,
        };
      }
      dialogVisible.value = true;
    };

    const saveForm = () => {
      formRef.value.validate(async (valid) => {
        if (!valid) return;
        saving.value = true;
        let options = {};
        try {
          options = formData.value.fujifilm_product_options_json ? JSON.parse(formData.value.fujifilm_product_options_json) : {};
        } catch (e) {
          notificationFire("Invalid JSON in product options", "error");
          saving.value = false;
          return;
        }
        const payload = {
          product_sku_id: formData.value.product_sku_id,
          fujifilm_product_id: formData.value.fujifilm_product_id,
          fujifilm_sku: formData.value.fujifilm_sku,
          base_price: formData.value.base_price,
          fujifilm_product_options: options,
          is_active: formData.value.is_active,
        };
        const request = formData.value.id
          ? ApiService.put("fujifilm-product-mappings/" + formData.value.id, payload)
          : ApiService.post("fujifilm-product-mappings", payload);
        request
          .then(({ data }) => {
            notificationFire(data.message, "success");
            dialogVisible.value = false;
            getMappings();
            saving.value = false;
          })
          .catch((error) => {
            const msg = error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Save failed";
            notificationFire(msg, "error");
            saving.value = false;
          });
      });
    };

    const remove = async (id, index) => {
      ElMessageBox.confirm(constants.general.DELETE_MESSAGE, constants.general.ALERT_WARNING, {
        confirmButtonText: constants.general.ALERT_CONFIMATION,
        cancelButtonText: constants.general.ALERT_CANCEL,
        type: "warning",
        center: true,
      }).then(() => {
        ApiService.delete("fujifilm-product-mappings/" + id)
          .then((response) => {
            notificationFire(response.data.message, "success");
            tableData.value.splice(index, 1);
            total.value = total.value > 0 ? total.value - 1 : 0;
          })
          .catch((error) => {
            const msg = error.response && error.response.data && error.response.data.message
              ? error.response.data.message
              : "Delete failed";
            notificationFire(msg, "error");
          });
      });
    };

    const handleSizeChange = (number) => {
      searchParams.recordsPerPage = number;
      handleCurrentChange(1);
    };

    const handleCurrentChange = (number) => {
      searchParams.pageNub = number;
      getMappings();
    };

    const handleSearch = () => {
      handleCurrentChange(1);
    };

    const resetSearch = () => {
      searchParams.search = "";
      handleCurrentChange(1);
    };

    onMounted(() => {
      setCurrentPageBreadcrumbs(formatText("fujifilm-product-mappings"), [
        { name: formatText("trading-partners"), path: "trading-partners" },
        { name: "Fujifilm SKU mappings", active: true },
      ]);
      let role = loggedInUserRoles();
      let per = loggedInUserPermissions();
      if (per.includes("updateTradingPartner") || role == "Super Admin") permission.isEditAllowed = true;
      if (per.includes("deleteTradingPartner") || role == "Super Admin") permission.isDeleteAllowed = true;
      searchProductSkus("");
      getMappings();
    });

    return {
      constants,
      loading,
      saving,
      total,
      tableData,
      tableHeader,
      searchParams,
      noDataFound,
      updateProps,
      permission,
      dialogVisible,
      formRef,
      formData,
      productSkuOptions,
      getMappings,
      searchProductSkus,
      openForm,
      saveForm,
      remove,
      handleSizeChange,
      handleCurrentChange,
      handleSearch,
      resetSearch,
    };
  },
};
</script>
